import React from 'react'
import axios from 'axios'
import { ExternalLink, Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import toastr from '@/lib/notifications'
import EnquiryLearnerSubjects from '../learner_subjects/index'
import EnquiryCoachbitOrders from '../coachbit_orders/index'
import EditEnquiryLearner from './edit'
import AddCoachbitSubscription from './coachbit'
import CopyToClipboard from '../../copy_to_clipboard'
import Modal from '../../modal'
import store from '../../../state/store'

class EnquiryLearnersPartial extends React.Component {

  constructor(props) {
    super(props)

    this.state = {
      editing: false,
      coachbitOpen: false,
      coachbitData: null
    }
  }

  onToggleEdit = (e) => {
    if (e) {
      e.preventDefault()
    }

    this.setState({editing: !this.state.editing})
  }

  onDone = () => {
    this.setState({editing: false})
  }

  onRemove = () => {
    store.emit('enquiry:learners:destroy', this.props.learner.enquiry_id, this.props.learner.id)
  }

  onAddSubject = () => {
    store.emit('enquiry:learner_subjects:create', this.props.enquiry_id, this.props.learner.id)
  }

  onOpenCoachbit = () => {
    axios.get(`/api/sales/enquiries/${this.props.learner.enquiry_id}/coachbit_orders/new`, {
      params: { enquiry_learner_id: this.props.learner.id }
    })
    .then((response) => {
      this.setState({coachbitOpen: true, coachbitData: response.data})
    })
    .catch((error) => {
      const message = (error && error.response && error.response.data && error.response.data.custom_error_message) || 'Failed to load Coachbit order data.'
      toastr.error(message)
    })
  }

  onOpenCoachbitOrder = (order_id) => {
    axios.get(`/api/sales/enquiries/${this.props.learner.enquiry_id}/coachbit_orders/${order_id}`, {
      params: { enquiry_learner_id: this.props.learner.id }
    })
    .then((response) => {
      this.setState({coachbitOpen: true, coachbitData: response.data})
    })
    .catch(() => {
      toastr.error('Failed to load Coachbit order data.')
    })
  }

  onCloseCoachbit = () => {
    this.setState({coachbitOpen: false, coachbitData: null})
  }

  onAddLearnerToProfile = () => {
    store.emit('enquiry:learners:create_learner', this.props.learner.id)
  }

  onLinkToPotentialMatch = () => {
    const match = this.props.learner.potential_match
    if (!match) return
    store.emit('enquiry:learners:link_to_customer_learner', this.props.learner.id, match.id)
  }

  renderFieldRow = (label, existingValue, enquiryValue) => {
    const value = existingValue || enquiryValue || ''
    const showDiff = existingValue && enquiryValue &&
      String(existingValue).trim().toLowerCase() !== String(enquiryValue).trim().toLowerCase()

    return (
      <React.Fragment>
        <div className="text-sm font-medium text-muted-foreground">{label}:</div>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm">{value}</span>
          {showDiff && (
            <span className="text-xs italic text-amber-700">enquired with {enquiryValue}</span>
          )}
        </div>
      </React.Fragment>
    )
  }

  render() {
    const learner = this.props.learner
    const existing = learner.existing_learner

    const displayName = existing
      ? [existing.first_name, existing.last_name].filter(Boolean).join(' ').trim()
      : learner.name

    const firstName = existing ? (existing.first_name || '') : (learner.first_name || (learner.name || '').trim().split(/\s+/)[0] || '')
    const lastName  = existing ? (existing.last_name  || '') : (learner.last_name  || (learner.name || '').trim().split(/\s+/).slice(1).join(' '))

    const potentialMatchName = learner.potential_match
      ? [learner.potential_match.first_name, learner.potential_match.last_name].filter(Boolean).join(' ')
      : null

    const coachbitOrders = learner.coachbit_orders || []

    return (
      <React.Fragment>
        <Card>
          <CardHeader className="flex flex-wrap items-center gap-2">
            <CardTitle className="flex min-w-0 items-center gap-2.5">
              <img src={learner.profile_picture_url} alt="Mug Shot" className="size-[42px] shrink-0 rounded-full"/>
              <CopyToClipboard content={displayName}>{displayName}</CopyToClipboard>
              {existing ? (
                <React.Fragment>
                  <Badge variant="success">Existing Learner</Badge>
                  <a
                    href={existing.profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open learner profile"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ExternalLink className="size-4"/>
                  </a>
                </React.Fragment>
              ) : (
                <Badge variant="warning">New Learner</Badge>
              )}
            </CardTitle>
            <CardAction className="ml-auto flex items-center gap-2 self-center">
              {!existing && !learner.potential_match && (
                <Button variant="outline" size="sm" onClick={this.onAddLearnerToProfile}>
                  Add Learner to Customer Profile
                </Button>
              )}
              <Button variant="secondary" size="icon-sm" title="Edit learner" onClick={this.onToggleEdit}><Pencil /></Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="destructive" size="icon-sm" title="Remove learner"><Trash2 /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>Cancel</DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onSelect={this.onRemove}>Confirm</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardAction>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {!existing && learner.potential_match && (
              <div className="rounded-[10px] border border-info/30 bg-info/10 px-4 py-3">
                <div className="text-sm">
                  This learner seems to match <strong>{potentialMatchName}</strong> on the customer's profile. Link them?
                </div>
                <div className="mt-2 flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(learner.potential_match.profile_url, '_blank', 'noopener,noreferrer')}
                  >
                    View Learner Profile
                  </Button>
                  <Button size="sm" onClick={this.onLinkToPotentialMatch}>Confirm</Button>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2">
              <div className="mb-4 md:mb-0 md:border-r md:border-border md:pr-4">
                {this.state.editing ? (
                  <EditEnquiryLearner
                    enquiry_id={this.props.learner.enquiry_id}
                    learner_id={this.props.learner.id}
                    is_existing={!!existing}
                    first_name={firstName}
                    last_name={lastName}
                    gender={existing ? existing.gender : this.props.learner.gender}
                    learner_level={this.props.learner.learner_level}
                    high_school={this.props.learner.high_school}
                    tertiary_education={this.props.learner.tertiary_education}
                    email_address={existing ? existing.email_address : this.props.learner.email_address}
                    mobile_number={existing ? existing.mobile_number : this.props.learner.mobile_number}
                    onDone={this.onDone}
                  />
                ) : (
                  <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-3">
                    {this.renderFieldRow('First Name', existing && existing.first_name, learner.first_name || (learner.name || '').trim().split(/\s+/)[0])}
                    {this.renderFieldRow('Last Name', existing && existing.last_name, learner.last_name || (learner.name || '').trim().split(/\s+/).slice(1).join(' '))}
                    {this.renderFieldRow('Gender', existing && existing.gender, learner.gender)}
                    {this.renderFieldRow('Level', null, learner.learner_level)}
                    {this.renderFieldRow('Highschool', null, learner.high_school)}
                    {this.renderFieldRow('Tertiary Education', null, learner.tertiary_education)}
                    {this.renderFieldRow('Email', existing && existing.email_address, learner.email_address)}
                    {this.renderFieldRow('Phone', existing && existing.mobile_number, learner.mobile_number)}
                  </div>
                )}
              </div>

              <div className="md:pl-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Subjects</div>
                {(this.props.learner_subjects || []).length > 0 ? (
                  <EnquiryLearnerSubjects
                    learner_subjects={this.props.learner_subjects}
                    subjects={this.props.subjects}
                    learner={this.props.learner}
                    learner_id={this.props.learner.id}
                    enquiry_id={this.props.enquiry_id}
                    country_code={this.props.country_code}
                    customer={this.props.customer}
                  />
                ) : (
                  <div className="mb-2 text-sm italic text-muted-foreground">No subjects yet.</div>
                )}
                <Button variant="outline" size="sm" className="mt-2" onClick={this.onAddSubject}>Add Subject</Button>

                {this.props.country_code === 'ZA' && (
                  <React.Fragment>
                    <div className="my-4 border-t border-border"></div>

                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Coachbit Orders</div>
                    {coachbitOrders.length > 0 ? (
                      <EnquiryCoachbitOrders
                        orders={coachbitOrders}
                        enquiry_id={this.props.learner.enquiry_id}
                        onOpen={this.onOpenCoachbitOrder}
                      />
                    ) : (
                      <div className="mb-2 text-sm italic text-muted-foreground">No Coachbit orders yet.</div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={this.onOpenCoachbit}
                    >
                      Add Coachbit Order
                    </Button>
                  </React.Fragment>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {this.state.coachbitOpen && (
          <Modal
            title={(this.state.coachbitData && this.state.coachbitData.order) ? 'Edit Coachbit Order' : 'Add Coachbit Order'}
            size="modal-xl"
            onClose={this.onCloseCoachbit}
          >
            <div className="p-6">
              <AddCoachbitSubscription
                data={this.state.coachbitData}
                enquiryId={this.props.learner.enquiry_id}
                enquiryLearnerId={this.props.learner.id}
                onDone={this.onCloseCoachbit}
              />
            </div>
          </Modal>
        )}
      </React.Fragment>
    )
  }
}

export default EnquiryLearnersPartial
